import React, { useState, useEffect, useMemo } from 'react';
import { View, Text } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { Query } from '@/utils/react-query/Queries';

const AddToRoleModal = ({ isOpen, onClose, onConfirm, selectedCount = 0, loading = false }) => {
    const [selectedRoles, setSelectedRoles] = useState([]);

    const { data: loggedInUser } = Query.people.loggedInUser();
    const rolesQuery = Query.roles.all({ user: loggedInUser, options: { enabled: isOpen } });

    // Filter out access control roles for non-admins (unless they manage that role)
    const filteredRoles = useMemo(() => {
        if (!rolesQuery.data) return [];
        if (loggedInUser?.isAdmin) return rolesQuery.data;
        const managingRoleIDs = loggedInUser?.ManagingRoles?.map(r => r.id) || [];
        return rolesQuery.data.filter(r => !r.isAccessControl || managingRoleIDs.includes(r.id));
    }, [rolesQuery.data, loggedInUser]);

    const queryResult = useMemo(() => ({
        ...rolesQuery,
        data: filteredRoles,
    }), [rolesQuery, filteredRoles]);

    useEffect(() => {
        if (isOpen) {
            setSelectedRoles([]);
        }
    }, [isOpen]);

    const handleConfirm = () => {
        if (selectedRoles.length === 0) return;
        onConfirm?.(selectedRoles);
    };

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={onClose}
            onCancel={onClose}
            title="Add to Role"
            subtitle={`Add ${selectedCount} ${selectedCount === 1 ? 'person' : 'people'} to ${selectedCount === 1 ? 'a role' : 'roles'}`}
            confirmText="Add"
            onConfirm={handleConfirm}
            loading={loading}
        >
            <View style={styles.content}>
                <Text style={styles.label}>Select roles</Text>
                <SheetSelector
                    type="roles"
                    title="Roles"
                    placeholder="Choose roles..."
                    multiple
                    value={selectedRoles}
                    onChange={setSelectedRoles}
                    queryResult={queryResult}
                    noDefaultTruncated
                />
            </View>
        </SheetModal>
    );
};

const styles = StyleSheet.create((theme) => ({
    content: {
        padding: 20,
        gap: 12,
    },
    label: {
        fontSize: 14,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
    },
}));

export default AddToRoleModal;
